import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../../core/services/auth.service';
import { AppointmentsService } from '../../../core/services/appointments.service';
import { User } from '../../../core/models/user.interface';
import { Appointment } from '../../../core/models/appointment.model';
import { LoaderService } from '../../../core/shared/services/loader.service';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface EarningsHistory {
  period: string;
  startDate: Date;
  endDate: Date;
  hours: number;
  total: number;
}

@Component({
  selector: 'app-ganancias',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './ganancias.component.html',
  styleUrl: './ganancias.component.scss'
})
export class GananciasComponent implements OnInit {
  private authService = inject(AuthService);
  private appointmentsService = inject(AppointmentsService);
  private loaderService = inject(LoaderService);

  user: User | null = null;
  currentDate: Date = new Date();
  selectedMonth: Date = new Date();
  
  // Datos del mes actual
  currentMonthHours: number = 0;
  costPerHour: number = 200;
  currentMonthTotal: number = 0;
  
  // Historial de ganancias
  earningsHistory: EarningsHistory[] = [];
  paginatedHistory: EarningsHistory[] = [];
  
  // Almacenar todas las citas para generar reportes
  allAppointments: Appointment[] = [];
  
  // Paginación
  currentPage: number = 1;
  pageSize: number = 10;
  totalPages: number = 1;
  totalResults: number = 0;

  // Modal de detalles
  showDetailsModal: boolean = false;
  selectedPeriod: EarningsHistory | null = null;

  // Nombres de meses en español
  monthNames: string[] = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  ngOnInit(): void {
    this.user = this.authService.getUser();
    if (!this.user) {
      console.error('No hay usuario autenticado');
      return;
    }

    // Obtener costo por hora del mentor
    this.costPerHour = this.getMentorCostPerHour();
    
    // Cargar datos del mes actual
    this.loadCurrentMonthData();
    
    // Cargar historial
    this.loadEarningsHistory();
  }

  /**
   * Obtener el costo por hora del mentor
   */
  getMentorCostPerHour(): number {
    if (this.user?.type?.expertCategory?.cost) {
      return this.user.type.expertCategory.cost;
    }
    // Valor por defecto
    return 200;
  }

  /**
   * Cargar datos del mes actual
   */
  loadCurrentMonthData(): void {
    if (!this.user?._id) return;

    this.loaderService.show();
    
    this.appointmentsService.findByCoach(this.user._id).subscribe({
      next: (response) => {
        if (response && response.data && Array.isArray(response.data)) {
          const currentMonth = this.selectedMonth.getMonth();
          const currentYear = this.selectedMonth.getFullYear();
          
          // Filtrar citas del mes actual y que estén completadas
          const monthAppointments = response.data.filter(appointment => {
            const appointmentDate = new Date(appointment.date);
            return appointmentDate.getMonth() === currentMonth &&
                   appointmentDate.getFullYear() === currentYear;
            // Asumimos que cada cita es de 1 hora
          });

          // Calcular horas del mes (cada cita = 1 hora)
          this.currentMonthHours = monthAppointments.length;
          this.currentMonthTotal = this.currentMonthHours * this.costPerHour;
        }
        this.loaderService.hide();
      },
      error: (error) => {
        console.error('Error al cargar datos del mes:', error);
        this.loaderService.hide();
      }
    });
  }

  /**
   * Cargar historial de ganancias
   */
  loadEarningsHistory(): void {
    if (!this.user?._id) return;

    this.appointmentsService.findByCoach(this.user._id).subscribe({
      next: (response) => {
        if (response && response.data && Array.isArray(response.data)) {
          // Guardar todas las citas para generar reportes
          this.allAppointments = response.data;
          
          // Agrupar por mes y año
          const earningsByMonth = this.groupAppointmentsByMonth(response.data);
          
          // Convertir a array de EarningsHistory
          this.earningsHistory = earningsByMonth.map(item => {
            const startDate = new Date(item.year, item.month, 1);
            const endDate = new Date(item.year, item.month + 1, 0);
            
            return {
              period: this.formatPeriod(startDate, endDate),
              startDate: startDate,
              endDate: endDate,
              hours: item.hours,
              total: item.hours * this.costPerHour
            };
          });

          // Ordenar por fecha descendente (más reciente primero)
          this.earningsHistory.sort((a, b) => b.startDate.getTime() - a.startDate.getTime());
          
          this.totalResults = this.earningsHistory.length;
          this.updatePagination();
        }
      },
      error: (error) => {
        console.error('Error al cargar historial:', error);
      }
    });
  }

  /**
   * Agrupar citas por mes y año
   */
  groupAppointmentsByMonth(appointments: Appointment[]): Array<{year: number, month: number, hours: number}> {
    const grouped: { [key: string]: number } = {};

    appointments.forEach(appointment => {
      const appointmentDate = new Date(appointment.date);
      const key = `${appointmentDate.getFullYear()}-${appointmentDate.getMonth()}`;
      
      if (!grouped[key]) {
        grouped[key] = 0;
      }
      // Cada cita = 1 hora
      grouped[key] += 1;
    });

    return Object.keys(grouped).map(key => {
      const [year, month] = key.split('-').map(Number);
      return {
        year,
        month,
        hours: grouped[key]
      };
    });
  }

  /**
   * Formatear período de facturación
   */
  formatPeriod(startDate: Date, endDate: Date): string {
    const startDay = startDate.getDate();
    const endDay = endDate.getDate();
    const monthName = this.monthNames[endDate.getMonth()];
    const year = endDate.getFullYear();
    
    // Formato: "1 de Sep al 30 de Sep 2025"
    const monthShort = this.getMonthShortName(monthName);
    return `${startDay} de ${monthShort} al ${endDay} de ${monthShort} ${year}`;
  }

  /**
   * Obtener nombre corto del mes
   */
  getMonthShortName(monthName: string): string {
    const shortNames: { [key: string]: string } = {
      'Enero': 'Ene',
      'Febrero': 'Feb',
      'Marzo': 'Mar',
      'Abril': 'Abr',
      'Mayo': 'May',
      'Junio': 'Jun',
      'Julio': 'Jul',
      'Agosto': 'Ago',
      'Septiembre': 'Sep',
      'Octubre': 'Oct',
      'Noviembre': 'Nov',
      'Diciembre': 'Dic'
    };
    return shortNames[monthName] || monthName.substring(0, 3);
  }

  /**
   * Navegar al mes anterior
   */
  previousMonth(): void {
    this.selectedMonth = new Date(this.selectedMonth.getFullYear(), this.selectedMonth.getMonth() - 1, 1);
    this.loadCurrentMonthData();
  }

  /**
   * Navegar al mes siguiente
   */
  nextMonth(): void {
    this.selectedMonth = new Date(this.selectedMonth.getFullYear(), this.selectedMonth.getMonth() + 1, 1);
    this.loadCurrentMonthData();
  }

  /**
   * Obtener nombre del mes actual
   */
  getCurrentMonthName(): string {
    return this.monthNames[this.selectedMonth.getMonth()];
  }

  /**
   * Calcular porcentaje para el gráfico circular
   */
  getCircularProgress(): string {
    // Asumimos un máximo de 40 horas como referencia
    const maxHours = 40;
    const percentage = Math.min((this.currentMonthHours / maxHours) * 100, 100);
    // Calcular la circunferencia: 2 * π * r (r = 130)
    const circumference = 2 * Math.PI * 130;
    // Calcular el valor del stroke-dasharray (porcentaje de la circunferencia)
    const progressLength = (percentage / 100) * circumference;
    // Retornar el string para stroke-dasharray
    return `${progressLength} ${circumference}`;
  }

  /**
   * Formatear número con decimales
   */
  formatCurrency(value: number): string {
    return value.toFixed(2);
  }

  /**
   * Ver detalles de un período
   */
  viewDetails(period: EarningsHistory): void {
    this.selectedPeriod = period;
    this.showDetailsModal = true;
  }

  /**
   * Cerrar modal de detalles
   */
  closeDetailsModal(): void {
    this.showDetailsModal = false;
    this.selectedPeriod = null;
  }

  /**
   * Obtener nombre del mes para el modal
   */
  getMonthNameForPeriod(period: EarningsHistory | null): string {
    if (!period) return '';
    return this.monthNames[period.startDate.getMonth()];
  }

  /**
   * Obtener año para el período
   */
  getYearForPeriod(period: EarningsHistory | null): number {
    if (!period) return 0;
    return period.startDate.getFullYear();
  }

  /**
   * Convertir blob a base64
   */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Obtener citas del período seleccionado
   */
  private getAppointmentsForPeriod(period: EarningsHistory): Appointment[] {
    return this.allAppointments.filter(appointment => {
      const appointmentDate = new Date(appointment.date);
      return appointmentDate >= period.startDate && appointmentDate <= period.endDate;
    }).sort((a, b) => {
      // Ordenar por fecha y hora
      const dateA = new Date(`${a.date} ${a.time}`);
      const dateB = new Date(`${b.date} ${b.time}`);
      return dateA.getTime() - dateB.getTime();
    });
  }

  /**
   * Formatear fecha para mostrar en el PDF
   */
  private formatDateForPdf(date: string): string {
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = this.monthNames[d.getMonth()];
    const year = d.getFullYear();
    return `${day} de ${month} ${year}`;
  }

  /**
   * Descargar reporte resumido del modal (formato del modal)
   */
  async downloadModalReport(period: EarningsHistory): Promise<void> {
    try {
      this.loaderService.show();

      // Crear documento PDF
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      let currentY = 10;
      const margin = 14;

      // Cargar y añadir el logo
      try {
        const response = await fetch("assets/img/phoenix-logo.png");
        const blob = await response.blob();
        const base64data = await this.blobToBase64(blob);
        doc.addImage(base64data, "PNG", margin, currentY, 60, 10);
        currentY += 20;
      } catch (error) {
        console.warn('No se pudo cargar el logo:', error);
        currentY += 10;
      }

      // Título "Detalles"
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text("Detalles", margin, currentY);
      currentY += 10;

      // Información de la empresa
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text("R.U.C. N.o 20609872463", margin, currentY);
      currentY += 5;
      doc.text("PHOENIX GROUP S.A.C.", margin, currentY);
      currentY += 5;
      doc.text("San Isidro, Lima, Perú", margin, currentY);
      currentY += 15;

      // Total del período (a la derecha)
      const monthName = this.getMonthNameForPeriod(period);
      doc.setFontSize(12);
      doc.setTextColor(50, 50, 50);
      doc.text(`Total ${monthName}`, pageWidth - margin, currentY, { align: 'right' });
      currentY += 7;
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(`S/${this.formatCurrency(period.total)}`, pageWidth - margin, currentY, { align: 'right' });
      currentY += 20;

      // Tabla de desglose (similar al modal)
      const tableData = [[
        `Ingresos ${monthName}`,
        `S/ ${this.formatCurrency(this.costPerHour)}`,
        `${period.hours} horas`,
        `S/ ${this.formatCurrency(period.total)}`
      ]];

      autoTable(doc, {
        startY: currentY,
        head: [['DESCRIPCIÓN', 'COSTO POR HORA', 'TOTAL DE HORAS', 'TOTAL DE INGRESOS']],
        body: tableData,
        theme: 'striped',
        headStyles: {
          fillColor: [220, 53, 69], // Rojo similar al tema
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 10
        },
        bodyStyles: {
          fontSize: 10,
          cellPadding: 4
        },
        columnStyles: {
          0: { cellWidth: 60, halign: 'left' },   // DESCRIPCIÓN
          1: { cellWidth: 40, halign: 'right' },  // COSTO POR HORA
          2: { cellWidth: 40, halign: 'center' }, // TOTAL DE HORAS
          3: { cellWidth: 42, halign: 'right' }   // TOTAL DE INGRESOS
        },
        margin: { left: margin, right: margin },
        styles: {
          cellPadding: 4
        }
      });

      // Obtener la posición Y después de la tabla
      const finalY = (doc as any).lastAutoTable.finalY || currentY + 30;
      currentY = finalY + 20;

      // Línea separadora
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, currentY, pageWidth - margin, currentY);
      currentY += 15;

      // Resumen
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text("Resumen", margin, currentY);
      currentY += 12;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`Costo por hora:`, margin, currentY);
      doc.text(`S/ ${this.formatCurrency(this.costPerHour)}`, pageWidth - margin, currentY, { align: 'right' });
      currentY += 8;

      doc.text(`Total de horas:`, margin, currentY);
      doc.text(`${period.hours}`, pageWidth - margin, currentY, { align: 'right' });
      currentY += 8;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text(`Total de ingresos:`, margin, currentY);
      doc.text(`S/ ${this.formatCurrency(period.total)}`, pageWidth - margin, currentY, { align: 'right' });
      currentY += 20;

      // Términos y condiciones
      if (currentY > pageHeight - 50) {
        doc.addPage();
        currentY = 20;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text("Términos y condiciones", margin, currentY);
      currentY += 7;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      const termsText = "Este documento es necesario para procesar tu pago. Asegúrate de enviarlo correctamente para recibir tus ganancias a tiempo.";
      const splitTerms = doc.splitTextToSize(termsText, pageWidth - 2 * margin);
      doc.text(splitTerms, margin, currentY);

      // Generar nombre del archivo
      const year = period.startDate.getFullYear();
      const fileName = `Detalles_${monthName}_${year}.pdf`;

      // Guardar PDF
      doc.save(fileName);
      
      this.loaderService.hide();
    } catch (error) {
      console.error('Error al generar el PDF del modal:', error);
      this.loaderService.hide();
      alert('Error al generar el reporte. Por favor, inténtalo de nuevo.');
    }
  }

  /**
   * Descargar reporte detallado de un período (con todas las reuniones)
   */
  async downloadReport(period: EarningsHistory): Promise<void> {
    try {
      this.loaderService.show();
      
      // Obtener citas del período
      const periodAppointments = this.getAppointmentsForPeriod(period);
      
      if (periodAppointments.length === 0) {
        this.loaderService.hide();
        alert('No hay citas para este período');
        return;
      }

      // Crear documento PDF
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      let currentY = 10;
      const margin = 14;

      // Cargar y añadir el logo
      try {
        const response = await fetch("assets/img/phoenix-logo.png");
        const blob = await response.blob();
        const base64data = await this.blobToBase64(blob);
        doc.addImage(base64data, "PNG", margin, currentY, 60, 10);
        currentY += 20;
      } catch (error) {
        console.warn('No se pudo cargar el logo:', error);
        currentY += 10;
      }

      // Información de la empresa
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text("R.U.C. N.o 20609872463", pageWidth - margin, currentY, { align: 'right' });
      currentY += 5;
      doc.text("PHOENIX GROUP S.A.C.", pageWidth - margin, currentY, { align: 'right' });
      currentY += 5;
      doc.text("San Isidro, Lima, Perú", pageWidth - margin, currentY, { align: 'right' });
      currentY += 15;

      // Título del reporte
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text("Reporte de Ganancias", margin, currentY);
      currentY += 10;

      // Período de facturación
      doc.setFontSize(12);
      doc.setTextColor(50, 50, 50);
      doc.text(`Período: ${period.period}`, margin, currentY);
      currentY += 15;

      // Tabla de reuniones
      const tableData = periodAppointments.map((appointment, index) => {
        const appointmentDate = new Date(appointment.date);
        const formattedDate = this.formatDateForPdf(appointment.date);
        const formattedTime = appointment.time || 'N/A';
        const customerName = appointment.customer?.name || 'N/A';
        const costPerHour = this.costPerHour.toFixed(2);
        const hours = '1 hora'; // Cada cita es de 1 hora
        const total = this.costPerHour.toFixed(2);

        return [
          (index + 1).toString(),
          formattedDate,
          formattedTime,
          customerName,
          `S/ ${costPerHour}`,
          hours,
          `S/ ${total}`
        ];
      });

      // Calcular ancho disponible para la tabla (página menos márgenes)
      // A4 tiene ~210mm de ancho, menos 28mm de márgenes = 182mm disponible
      const availableWidth = pageWidth - (2 * margin);
      
      // Agregar tabla con anchos ajustados y optimizados
      autoTable(doc, {
        startY: currentY,
        head: [['#', 'Fecha', 'Hora', 'Cliente', 'Costo/Hora', 'Horas', 'Total']],
        body: tableData,
        theme: 'striped',
        headStyles: {
          fillColor: [220, 53, 69], // Rojo similar al tema
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 9
        },
        bodyStyles: {
          fontSize: 8,
          cellPadding: 2
        },
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' },   // #
          1: { cellWidth: 32, halign: 'left' },     // Fecha
          2: { cellWidth: 20, halign: 'center' },   // Hora
          3: { cellWidth: 45, halign: 'left' },     // Cliente
          4: { cellWidth: 22, halign: 'right' },    // Costo/Hora
          5: { cellWidth: 18, halign: 'center' },   // Horas
          6: { cellWidth: 22, halign: 'right' }     // Total
        },
        margin: { left: margin, right: margin },
        styles: {
          cellPadding: 2,
          overflow: 'linebreak',
          font: 'helvetica',
          fontSize: 8
        },
        didParseCell: function (data: any) {
          // Truncar texto largo en la columna Cliente si es necesario
          if (data.column.index === 3 && data.cell.text) {
            const text = Array.isArray(data.cell.text) ? data.cell.text[0] : data.cell.text;
            if (text && text.length > 25) {
              data.cell.text = [text.substring(0, 23) + '...'];
            }
          }
        }
      });

      // Obtener la posición Y después de la tabla
      const finalY = (doc as any).lastAutoTable.finalY || currentY + 50;
      currentY = finalY + 15;

      // Verificar si necesitamos una nueva página
      if (currentY > pageHeight - 50) {
        doc.addPage();
        currentY = 20;
      }

      // Línea separadora
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, currentY, pageWidth - margin, currentY);
      currentY += 10;

      // Resumen
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text("Resumen", margin, currentY);
      currentY += 10;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`Costo por hora:`, margin, currentY);
      doc.text(`S/ ${this.costPerHour.toFixed(2)}`, pageWidth - margin, currentY, { align: 'right' });
      currentY += 7;

      doc.text(`Total de horas:`, margin, currentY);
      doc.text(`${period.hours} horas`, pageWidth - margin, currentY, { align: 'right' });
      currentY += 7;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text(`Total de ingresos:`, margin, currentY);
      doc.text(`S/ ${period.total.toFixed(2)}`, pageWidth - margin, currentY, { align: 'right' });
      currentY += 15;

      // Términos y condiciones
      if (currentY > pageHeight - 40) {
        doc.addPage();
        currentY = 20;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text("Términos y condiciones", margin, currentY);
      currentY += 7;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      const termsText = "Este documento es necesario para procesar tu pago. Asegúrate de enviarlo correctamente para recibir tus ganancias a tiempo.";
      const splitTerms = doc.splitTextToSize(termsText, pageWidth - 2 * margin);
      doc.text(splitTerms, margin, currentY);

      // Generar nombre del archivo
      const monthName = this.getMonthNameForPeriod(period);
      const year = period.startDate.getFullYear();
      const fileName = `Reporte_Ganancias_${monthName}_${year}.pdf`;

      // Guardar PDF
      doc.save(fileName);
      
      this.loaderService.hide();
    } catch (error) {
      console.error('Error al generar el PDF:', error);
      this.loaderService.hide();
      alert('Error al generar el reporte. Por favor, inténtalo de nuevo.');
    }
  }

  /**
   * Actualizar paginación
   */
  updatePagination(): void {
    this.totalPages = Math.ceil(this.earningsHistory.length / this.pageSize);
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedHistory = this.earningsHistory.slice(startIndex, endIndex);
  }

  /**
   * Cambiar de página
   */
  changePage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePagination();
    }
  }

  /**
   * Manejar click en número de página
   */
  onPageClick(page: number | string): void {
    if (page !== '...' && typeof page === 'number') {
      this.changePage(page);
    }
  }

  /**
   * Cambiar tamaño de página
   */
  changePageSize(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.updatePagination();
  }

  /**
   * Obtener array de páginas para mostrar
   */
  getPagesArray(): (number | string)[] {
    const pages: (number | string)[] = [];
    const maxVisible = 5;
    
    if (this.totalPages <= maxVisible) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (this.currentPage <= 3) {
        for (let i = 1; i <= 3; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(this.totalPages);
      } else if (this.currentPage >= this.totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = this.totalPages - 2; i <= this.totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = this.currentPage - 1; i <= this.currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(this.totalPages);
      }
    }
    
    return pages;
  }
}
